import { isEmpty } from 'ramda';
import {
  Table,
  SORT_DIRECTION,
  WithTableLayout,
  TableFooter,
  TABLE_ACTION,
  TABLE_ACTION_STATE,
  SortModel
} from '@qrios/frontend-library';
import { ReactNode, useContext, useEffect, useState } from 'react';

import { Dialog } from '../dialog';
import TableFooterInfo from '../table/footerInfo';
import { isEmptyOrNil } from '../../../constants/helpers/utils';

import {
  Filters,
  TableData,
  ReportsData,
  HandleRetryReportRequest,
  HandleCancelReportRequest,
  HandleDeleteReportRequest,
  HandleGenerateReportRequest,
  HandleRegenerateReportRequest
} from './interfaces';
import { columns } from './columns';
import Details from './actions/details';
import RetryReportDialog from './actions/retry/dialog';
import CancelReportDialog from './actions/cancel/dialog';
import DeleteReportDialog from './actions/delete/dialog';
import RegenerateReportDialog from './actions/regenerate/dialog';
import GenerateReport from './generate-report';
import { LocalesContext } from './localesContext';
import { getWarningMessage } from './utils/getWarningMsg';
import { getFooterInfoProps } from './utils/getFooterInfo';
import { mapReportToTableStructure } from './utils/mapReportToTableStructure';
import { useRetryReport } from './actions/retry/useRetryReport';
import { useDeleteReport } from './actions/delete/useDeleteReport';
import { useCancelReport } from './actions/cancel/useCancelReport';
import { useReportDetails } from './actions/details/useReportDetails';
import { useGenerateReport } from './generate-report/useGenerateReport';
import { useRegenerateReport } from './actions/regenerate/useRegenerateReport';

export interface CommonProps<T> {
  data: ReportsData;
  emptyMsg: ReactNode;
  error?: ReactNode;
  loading: boolean;
  refresh: () => Promise<void>;
  handleDeleteReportRequest: HandleDeleteReportRequest;
  handleCancelReportRequest: HandleCancelReportRequest;
  handleRetryReportRequest: HandleRetryReportRequest;
  handleRegenerateReportRequest: HandleRegenerateReportRequest;
  handleGenerateReportRequest: HandleGenerateReportRequest<T>;
  handleDownloadReport: (id: string, name: string) => void;
  generationAllowed: boolean;
  filters: Filters;
}

const ReportsComponent = ({
  data,
  emptyMsg,
  error,
  loading,
  refresh,
  handleDeleteReportRequest,
  handleCancelReportRequest,
  handleRetryReportRequest,
  handleRegenerateReportRequest,
  handleGenerateReportRequest,
  handleDownloadReport,
  generationAllowed,
  filters
}: CommonProps<unknown>) => {
  const locales = useContext(LocalesContext);
  const [reports, setReports] = useState<TableData[]>([]);
  const [sortModel, setSortModel] = useState<SortModel>([{ field: 'createDate', sort: SORT_DIRECTION.ASC }]);

  const footerInfoProps = getFooterInfoProps(loading, data.limits);

  const {
    handleOpenDeleteDialog,
    handleCloseDeleteDialog,
    openDeleteDialog,
    deletingReport,
    handleDeleteReport,
    deleteReportError,
    isReportDeleting
  } = useDeleteReport({
    handleDeleteReportRequest,
    handleSuccess: () => {
      refresh();
    }
  });

  const {
    handleOpenCancelDialog,
    handleCloseCancelDialog,
    openCancelDialog,
    cancellingReport,
    handleCancelReport,
    cancelReportError,
    isReportCancelling
  } = useCancelReport({
    handleCancelReportRequest,
    handleSuccess: () => {
      refresh();
    }
  });

  const {
    openDetailsDialog,
    handleOpenDetailsDialog,
    selectedReportDetails,
    handleCloseDetailsDialog
  } = useReportDetails();

  const {
    handleOpenRetryDialog,
    openRetryDialog,
    handleCloseRetryDialog,
    handleRetryReport,
    isReportRetried,
    retriedReport,
    retryReportError
  } = useRetryReport({
    handleRetryReportRequest,
    handleSuccess: () => {
      refresh();
    }
  });

  const {
    handleCloseRegenerateDialog,
    handleOpenRegenerateDialog,
    handleRegenerateReport,
    regeneratingReport,
    openRegenerateDialog,
    isReportRegenerating,
    regenerateReportError
  } = useRegenerateReport({
    handleRegenerateReportRequest,
    handleSuccess: () => {
      refresh();
    }
  });

  const {
    handleGenerateReportModalOpen,
    handleGenerateReportModalClose,
    generateReportModalOpen,
    handleGenerateReport,
    handleOpenNextPage,
    generateReportState,
    isGenerating
  } = useGenerateReport({
    handleGenerateReportRequest,
    handleClose: () => {
      filters.clearValues();
    },
    handleSuccess: () => {
      refresh();
    },
    pendingLimitReached: !generationAllowed,
    loading,
    predefinedReportGeneration: filters.predefinedReportGeneration,
    filtersValidationSchema: filters.validation
  });

  useEffect(() => {
    if (isEmpty(data.reports)) {
      setReports([]);
    }

    if (!isEmptyOrNil(data.reports)) {
      const mappedReports = data.reports.map(item =>
        mapReportToTableStructure(
          item,
          generationAllowed,
          handleOpenDeleteDialog,
          handleOpenCancelDialog,
          handleOpenDetailsDialog,
          handleOpenRegenerateDialog,
          handleOpenRetryDialog,
          handleDownloadReport
        )
      );
      setReports(mappedReports);
    }
  }, [
    data.reports,
    generationAllowed,
    handleOpenCancelDialog,
    handleOpenDeleteDialog,
    handleOpenDetailsDialog,
    handleOpenRegenerateDialog,
    handleOpenRetryDialog,
    handleDownloadReport
  ]);

  return (
    <WithTableLayout>
      <Table
        dataGridProps={{
          rows: reports,
          loading,
          error: { message: error, refresh },
          columns,
          sortModel,
          onSortModelChange: (model: SortModel) => setSortModel(model),
          emptyMsg
        }}
        headProps={{
          warningMsg: getWarningMessage({
            loading,
            status: data?.limits?.status
          })
        }}
      />
      {!error && (
        <TableFooter
          tableInfo={<TableFooterInfo {...footerInfoProps} />}
          refreshProps={{ disabled: loading, handler: refresh }}
          reportProps={{
            disabled: loading,
            handler: handleGenerateReportModalOpen,
            name: TABLE_ACTION.REPORT,
            state: generationAllowed ? TABLE_ACTION_STATE.ACTIVE : TABLE_ACTION_STATE.DISABLED,
            warningMsg: getWarningMessage({
              loading,
              status: data?.limits?.status
            })
          }}
        />
      )}
      <Dialog
        handleClose={handleGenerateReportModalClose}
        open={generateReportModalOpen}
        title={locales.dialog.title.generateReport}
      >
        <GenerateReport
          filtersForm={filters.form}
          handleGenerateReport={handleGenerateReport}
          handleOpenNextPage={handleOpenNextPage}
          state={generateReportState}
          isGenerating={isGenerating}
          initialValues={filters.initialValues}
        />
      </Dialog>
      <Dialog handleClose={handleCloseDeleteDialog} open={openDeleteDialog} title={locales.dialog.title.deleteReport}>
        <DeleteReportDialog
          handleDeleteReport={handleDeleteReport}
          handleCloseDeleteDialog={handleCloseDeleteDialog}
          report={deletingReport}
          error={deleteReportError}
          loading={isReportDeleting}
        />
      </Dialog>
      <Dialog handleClose={handleCloseCancelDialog} open={openCancelDialog} title={locales.dialog.title.cancelReport}>
        <CancelReportDialog
          handleCancelReport={handleCancelReport}
          handleCloseCancelDialog={handleCloseCancelDialog}
          report={cancellingReport}
          loading={isReportCancelling}
          error={cancelReportError}
        />
      </Dialog>
      <Dialog handleClose={handleCloseDetailsDialog} open={openDetailsDialog} title={selectedReportDetails?.name ?? ''}>
        <Details
          report={selectedReportDetails}
          generationAllowed={generationAllowed}
          handleOpenDeleteDialog={handleOpenDeleteDialog}
          handleOpenCancelDialog={handleOpenCancelDialog}
          handleCloseDetailsDialog={handleCloseDetailsDialog}
          handleOpenRegenerateDialog={handleOpenRegenerateDialog}
          handleOpenRetryDialog={handleOpenRetryDialog}
          handleDownloadReport={handleDownloadReport}
          appliedFilters={filters.details}
        />
      </Dialog>
      <Dialog
        handleClose={handleCloseRegenerateDialog}
        open={openRegenerateDialog}
        title={locales.dialog.title.regenerateReport}
      >
        <RegenerateReportDialog
          handleRegenerateReport={handleRegenerateReport}
          handleCloseRegenerateReport={handleCloseRegenerateDialog}
          report={regeneratingReport}
          error={regenerateReportError}
          loading={isReportRegenerating}
          filtersSummary={filters.summary}
        />
      </Dialog>
      <Dialog handleClose={handleCloseRetryDialog} open={openRetryDialog} title={locales.dialog.title.retryReport}>
        <RetryReportDialog
          handleRetryReport={handleRetryReport}
          handleCloseRetryReport={handleCloseRetryDialog}
          report={retriedReport}
          loading={isReportRetried}
          filtersSummary={filters.summary}
          error={retryReportError}
        />
      </Dialog>
    </WithTableLayout>
  );
};

export default ReportsComponent;
